import React, { useRef, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import MarkdownRenderer from "@/components/MarkdownRenderer";

interface LeadManagementSectionProps {
  registerSectionRef: (id: string, element: HTMLElement | null) => void;
}

const leadDetailsMarkdown = `
When viewing a lead's details, you'll see the following sections:

- **Overview**: General information, tasks, and enrichment controls for the lead
- **Notes**: Add and view detailed notes about the lead
- **Documents**: Upload and manage documents related to the lead
- **Contacts**: Manage key contact people for this lead
- **Activity**: Timeline of all interactions and changes
- **Reviews**: Customer reviews related to the lead
- **Score**: Lead scoring breakdown and history
- **Structured View**: Consolidated enrichment data organized into clear categories
`;

const activitiesMarkdown = `
Discovery Hub tracks various activity types including:

- **Notes**: Notes added to the lead
- **Status Changes**: Updates to lead status
- **Documents**: Documents added or changed
- **Tasks**: Task creation and completion
- **Communications**: Email and call interactions
- **Score Updates**: Changes to lead score
`;

const csvFeatureMarkdown = `
Discovery Hub supports bulk lead management through CSV import and export:

- **CSV Import**: Import multiple leads at once from a CSV file
  - Required fields: name (other fields are optional)
  - Supported fields: name, address, phone, email, website, category, status, statusNotes
  - Status notes are automatically linked to the lead
  - Error reporting for invalid or incomplete entries
  - Quota limits apply based on your subscription

- **CSV Export**: Export your leads to CSV format
  - Basic export: includes essential lead information
  - Full export: includes all available lead data
  - Status notes are included in exports
  - Downloadable CSV file for easy sharing and backup
`;

const LeadManagementSection = ({ registerSectionRef }: LeadManagementSectionProps) => {
  const overviewRef = useRef<HTMLHeadingElement>(null);
  const addingLeadsRef = useRef<HTMLHeadingElement>(null);
  const leadDetailsRef = useRef<HTMLHeadingElement>(null);
  const leadEnrichmentRef = useRef<HTMLHeadingElement>(null);
  const leadScoringRef = useRef<HTMLHeadingElement>(null);
  const leadActivitiesRef = useRef<HTMLHeadingElement>(null);
  
  useEffect(() => {
    registerSectionRef('leads-overview', overviewRef.current);
    registerSectionRef('adding-leads', addingLeadsRef.current);
    registerSectionRef('lead-details', leadDetailsRef.current);
    registerSectionRef('lead-enrichment', leadEnrichmentRef.current);
    registerSectionRef('lead-scoring', leadScoringRef.current);
    registerSectionRef('lead-activities', leadActivitiesRef.current);
  }, [registerSectionRef]);

  return (
    <>
      <h2 
        id="leads-overview" 
        ref={overviewRef}
        className="text-2xl font-bold tracking-tight scroll-mt-16"
      >
        Lead Management Overview
      </h2>
      <Card>
        <CardHeader>
          <CardTitle>Managing Leads</CardTitle>
          <CardDescription>Discover how to effectively manage your sales leads</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>
            The Leads section is the central hub for managing all your potential customers within your company context. 
            Here you can view, organize, filter, and update your leads throughout the sales process.
          </p>
          <div className="space-y-2">
            <h4 className="font-semibold">Available Views</h4>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Card View:</strong> Shows leads in card format with visual information</li>
              <li><strong>List View:</strong> Displays leads in a compact list format</li>
              <li><strong>Table View:</strong> Provides a detailed tabular view with sortable columns</li>
            </ul>
          </div>
          <div className="space-y-2 mt-4">
            <h4 className="font-semibold">Filtering and Searching</h4>
            <p>
              Use the search bar and filters to quickly find specific leads based on status, score, category, 
              creation date, and other attributes.
            </p>
          </div>
          <div className="space-y-2 mt-4">
            <h4 className="font-semibold">Lead Organization</h4>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Favorites:</strong> Mark important leads as favorites for quick access</li>
              <li><strong>Status Tracking:</strong> Monitor leads through the sales pipeline using statuses</li>
              <li><strong>Company Association:</strong> All leads belong to your current active company</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      <h2 
        id="adding-leads" 
        ref={addingLeadsRef}
        className="text-2xl font-bold tracking-tight mt-8 scroll-mt-16"
      >
        Adding Leads
      </h2>
      <Card>
        <CardHeader>
          <CardTitle>Creating New Leads</CardTitle>
          <CardDescription>How to add new leads to your pipeline</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>
            You can add new leads to Discovery Hub in several ways:
          </p>
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold">Place Search</h4>
              <p>
                Use the Add Leads button in the header to find businesses and automatically create leads:
              </p>
              <ol className="list-decimal pl-6 space-y-1">
                <li>Navigate to the Places search section</li>
                <li>Enter search criteria (location, business type, etc.)</li>
                <li>Browse results and click "Add as Lead" for businesses you want to track</li>
              </ol>
            </div>

            <div className="mt-4">
              <h4 className="font-semibold">CSV Import and Export</h4>
              <p>
                Efficiently manage multiple leads using CSV files:
              </p>
              <ol className="list-decimal pl-6 space-y-1">
                <li>Access CSV options from the leads management view</li>
                <li>Use the import button to upload your CSV file</li>
                <li>Export your leads using the export button</li>
                <li>Download a CSV template to see the correct format</li>
              </ol>
              
              <div className="mt-4">
                <MarkdownRenderer content={csvFeatureMarkdown} />
              </div>
            </div>

            <div className="mt-4">
              <h4 className="font-semibold">Lead Import Limits</h4>
              <p>
                The number of leads you can add depends on your subscription plan. The system will notify you 
                when you approach your lead quota limit.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <h2 
        id="lead-details" 
        ref={leadDetailsRef}
        className="text-2xl font-bold tracking-tight mt-8 scroll-mt-16"
      >
        Lead Details
      </h2>
      <Card>
        <CardHeader>
          <CardTitle>Lead Detail View</CardTitle>
          <CardDescription>Understanding the lead details page</CardDescription>
        </CardHeader>
        <CardContent>
          <MarkdownRenderer content={leadDetailsMarkdown} />
          
          <div className="mt-4 space-y-2">
            <h4 className="font-semibold">Lead Status Updates</h4>
            <p>
              You can update a lead's status to track its progression through your sales pipeline:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>New:</strong> Recently added leads</li>
              <li><strong>Contacted:</strong> Leads that have been reached out to</li>
              <li><strong>Qualified:</strong> Leads that meet your qualification criteria</li>
              <li><strong>Proposal:</strong> Leads that have received a formal proposal</li>
              <li><strong>Negotiation:</strong> In active price/terms discussion</li>
              <li><strong>Closed Won:</strong> Successfully converted leads</li>
              <li><strong>Closed Lost:</strong> Unconverted leads</li>
            </ul>
          </div>

          <div className="mt-4 space-y-2">
            <h4 className="font-semibold">Quick Actions</h4>
            <p>
              The lead details page provides quick action buttons to:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Add notes</li>
              <li>Create tasks</li>
              <li>Upload documents</li>
              <li>Send email templates</li>
              <li>Add contacts</li>
              <li>Access call scripts</li>
            </ul>
          </div>

          <div className="mt-4 space-y-2">
            <h4 className="font-semibold">Enrichment & Structured View</h4>
            <p>
              From the Overview tab, you can trigger multi-stage enrichment to automatically gather detailed 
              information about the lead. The Structured View tab then presents all enrichment data in an 
              organized format. See the <strong>Lead Enrichment</strong> section below for full details.
            </p>
          </div>
        </CardContent>
      </Card>
      
      <h2 
        id="lead-enrichment" 
        ref={leadEnrichmentRef}
        className="text-2xl font-bold tracking-tight mt-8 scroll-mt-16"
      >
        Lead Enrichment
      </h2>
      <Card>
        <CardHeader>
          <CardTitle>Multi-Stage Enrichment</CardTitle>
          <CardDescription>Automatically gather detailed intelligence about your leads</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>
            Discovery Hub uses a multi-stage enrichment pipeline to build a comprehensive profile for each lead. 
            You can trigger enrichment from the lead's Overview tab, and progress is tracked in real time.
          </p>
          <div className="space-y-2">
            <h4 className="font-semibold">Enrichment Stages</h4>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Web Search:</strong> AI-powered research that gathers company details, contact information, industry data, and online presence from public sources</li>
              <li><strong>Deep Research:</strong> In-depth analysis covering market position, reputation, Google reviews, recent news, and detailed website content</li>
              <li><strong>LinkedIn Enrichment:</strong> Company firmographics, employee count, specialties, and key personnel from LinkedIn profiles</li>
            </ul>
          </div>
          <div className="space-y-2 mt-4">
            <h4 className="font-semibold">Data Quality Score</h4>
            <p>
              After enrichment, each lead receives a quality score from 0 to 100, evaluated across five dimensions:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Contact Info:</strong> Availability and completeness of contact details</li>
              <li><strong>Company Info:</strong> Business description, industry, and size</li>
              <li><strong>Online Presence:</strong> Website, social media, and directory listings</li>
              <li><strong>Services & Products:</strong> Clarity of offerings and specializations</li>
              <li><strong>Team & Leadership:</strong> Key personnel and organizational information</li>
            </ul>
            <p className="mt-2">
              The overall score is displayed as a badge on the lead card and reflects the best data gathered across all enrichment stages.
            </p>
          </div>
          <div className="space-y-2 mt-4">
            <h4 className="font-semibold">Structured View</h4>
            <p>
              The Structured View tab on the lead details page presents all enrichment data in a consolidated, organized layout — 
              including company information, staff and team members, customer reviews, business history, and more.
            </p>
          </div>
        </CardContent>
      </Card>

      <h2 
        id="lead-scoring" 
        ref={leadScoringRef}
        className="text-2xl font-bold tracking-tight mt-8 scroll-mt-16"
      >
        Lead Scoring
      </h2>
      <Card>
        <CardHeader>
          <CardTitle>Lead Scoring System</CardTitle>
          <CardDescription>How leads are scored and prioritized</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>
            Discovery Hub uses an advanced lead scoring algorithm to help you identify the most promising leads. Scores range 
            from 0 to 100, with higher scores indicating higher-quality leads.
          </p>
          <div className="space-y-2">
            <h4 className="font-semibold">Scoring Components</h4>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Demographic Score (0-50):</strong> Business details, contact completeness</li>
              <li><strong>Engagement Score (0-60):</strong> Status progression and interaction level</li>
              <li><strong>Activity Score (0-50):</strong> Notes, documents, and recorded interactions</li>
              <li><strong>Recency Score (0-30):</strong> How recently you've engaged with the lead</li>
            </ul>
          </div>
          <div className="space-y-2 mt-4">
            <h4 className="font-semibold">Score Categories</h4>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Cold (0-39):</strong> New leads with minimal information</li>
              <li><strong>Warm (40-69):</strong> Leads showing interest with some engagement</li>
              <li><strong>Hot (70-89):</strong> Qualified leads with strong engagement</li>
              <li><strong>Very Hot (90-100):</strong> Highly qualified leads nearing conversion</li>
            </ul>
          </div>
          <div className="space-y-2 mt-4">
            <h4 className="font-semibold">Score Breakdown and History</h4>
            <p>
              View detailed breakdowns of what factors contribute to a lead's score and track score history 
              over time through the Score tab on the lead details page.
            </p>
          </div>
          <div className="space-y-2 mt-4">
            <h4 className="font-semibold">Score Recalculation</h4>
            <p>
              Manually trigger score recalculation to update a lead's score based on recent changes and activities.
            </p>
          </div>
        </CardContent>
      </Card>
      
      <h2 
        id="lead-activities" 
        ref={leadActivitiesRef}
        className="text-2xl font-bold tracking-tight mt-8 scroll-mt-16"
      >
        Lead Activities
      </h2>
      <Card>
        <CardHeader>
          <CardTitle>Activity Tracking</CardTitle>
          <CardDescription>Managing interactions and activities with leads</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>
            Discovery Hub provides comprehensive activity tracking to maintain a complete history of all interactions with each lead.
          </p>
          
          <MarkdownRenderer content={activitiesMarkdown} />
          
          <div className="space-y-2 mt-4">
            <h4 className="font-semibold">Activity Timeline</h4>
            <p>
              The Activity tab on the lead details page shows a chronological timeline of all activities, 
              making it easy to track the history of your engagement with the lead.
            </p>
          </div>
          
          <div className="space-y-2 mt-4">
            <h4 className="font-semibold">Task Management</h4>
            <p>
              Create and track tasks related to leads:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Set task priorities (low, medium, high)</li>
              <li>Assign due dates</li>
              <li>Track task status (pending, in progress, completed, canceled)</li>
              <li>Add detailed descriptions</li>
            </ul>
          </div>
          
          <div className="space-y-2 mt-4">
            <h4 className="font-semibold">AI-Powered Outreach</h4>
            <p>
              Discovery Hub generates personalized email templates and call scripts using AI, drawing on 
              enrichment data such as firmographics, reviews, recent news, and LinkedIn insights.
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Emails follow a professional B2B structure: Observation, Relevance, Specific Value, and Direct CTA</li>
              <li>Messages are addressed to the most relevant key contact by name with formal salutations</li>
              <li>Regional spelling conventions are applied based on the lead's locale</li>
              <li>Call scripts follow the same contextual, data-driven approach</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </>
  );
};

export default LeadManagementSection;
